import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { initializeApp, getApps } from "firebase/app";
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { ArrowLeft, AtSign, Check, ChevronDown, KeyRound, LogOut, ShieldCheck, UserRound } from "lucide-react";
import type { AcademyAccountSession } from "@tradvera/academy-domain";
import { isTradveraOwnerEmail, localPreviewAllowed } from "./access";
import { PublicLanding } from "./PublicLanding";

const firebaseConfig = {
  apiKey: "AIzaSyBPfHMoEMDvMDK7jkAA9deSV4QmapjOIzs",
  authDomain: "tradvera-dev.firebaseapp.com",
  projectId: "tradvera-dev",
  storageBucket: "tradvera-dev.firebasestorage.app",
  messagingSenderId: "577677575136",
  appId: "1:577677575136:web:65018fd6ac31f42b9da185",
};

const app = getApps()[0] ?? initializeApp(firebaseConfig);
const auth = getAuth(app);
auth.languageCode = "ar";
const PREVIEW_KEY = "tradvera.auth.local-preview.v1";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  preview: boolean;
  session: AcademyAccountSession | null;
  enterPreview: () => void;
  exitPreview: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(() => {
    try { return localPreviewAllowed() && window.localStorage.getItem(PREVIEW_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    let unsubscribe = () => {};
    void setPersistence(auth, browserLocalPersistence).finally(() => {
      unsubscribe = onAuthStateChanged(auth, (next) => {
        if (next && !isTradveraOwnerEmail(next.email)) {
          setUser(null);
          setPreview(false);
          setLoading(false);
          void firebaseSignOut(auth);
          return;
        }
        setUser(next);
        if (next) {
          setPreview(false);
          try { window.localStorage.removeItem(PREVIEW_KEY); } catch { /* optional cache */ }
        }
        setLoading(false);
      });
    });
    return () => unsubscribe();
  }, []);

  const session = useMemo<AcademyAccountSession | null>(() => user ? {
    userId: user.uid,
    email: user.email,
    displayName: user.displayName,
    emailVerified: user.emailVerified,
    getIdToken: (forceRefresh = false) => user.getIdToken(forceRefresh),
  } : null, [user]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    preview,
    session,
    enterPreview: () => {
      if (!localPreviewAllowed()) return;
      setPreview(true);
      try { window.localStorage.setItem(PREVIEW_KEY, "1"); } catch { /* in-memory preview still works */ }
    },
    exitPreview: () => {
      setPreview(false);
      try { window.localStorage.removeItem(PREVIEW_KEY); } catch { /* no-op */ }
    },
    signOut: async () => {
      await firebaseSignOut(auth);
      setPreview(false);
      try { window.localStorage.removeItem(PREVIEW_KEY); } catch { /* no-op */ }
    },
  }), [user, loading, preview, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is required");
  return value;
}

export function AuthBoundary({ children }: { children: ReactNode }) {
  const { user, loading, preview, enterPreview } = useAuth();
  const [accessOpen, setAccessOpen] = useState(() => {
    const route = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    return route !== "/" || params.get("access") === "owner";
  });
  const forcePublic = window.location.pathname === "/" && new URLSearchParams(window.location.search).get("view") === "public";
  useEffect(() => {
    const syncRoute = () => {
      const params = new URLSearchParams(window.location.search);
      setAccessOpen(window.location.pathname !== "/" || params.get("access") === "owner");
    };
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);
  const openAccess = () => {
    window.history.pushState({}, "", "/?access=owner");
    setAccessOpen(true);
  };
  const closeAccess = () => {
    window.history.pushState({}, "", "/");
    setAccessOpen(false);
  };
  if (loading) return <AuthLoading />;
  if (forcePublic) return <PublicLanding onEnter={openAccess} />;
  if (!user && !preview) return accessOpen ? <AuthEntry onPreview={enterPreview} onBack={closeAccess} /> : <PublicLanding onEnter={openAccess} />;
  return <>{children}</>;
}

export function AccountControl() {
  const { user, preview, signOut, exitPreview } = useAuth();
  const [open, setOpen] = useState(false);
  const initial = user?.email?.trim().charAt(0).toUpperCase() || "ت";
  const onAdminRoute = typeof window !== "undefined" && (window.location.pathname === "/admin" || window.location.pathname.startsWith("/admin/"));
  return <div className="account-control">
    <button className={`account-trigger${user ? " is-verified" : ""}`} onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label="الحساب والحفظ">
      <span>{initial}</span><i/><ChevronDown size={12}/>
    </button>
    {open && <div className="account-popover">
      <small>{user ? "TRADVERA ID" : "FOUNDER PREVIEW"}</small>
      <h3>{user?.email ?? "معاينة محلية"}</h3>
      <p>{user ? "حساب المالك موثّق؛ تقدم الأكاديمية يُحفظ ويُستعاد تلقائيًا." : "معاينة تطوير محلية؛ غير متاحة على الموقع المنشور."}</p>
      {user && !onAdminRoute && <a className="account-admin-link" href="/admin"><ShieldCheck size={14}/> لوحة إدارة Tradvera</a>}
      {user ? <button className="account-exit" onClick={() => void signOut()}><LogOut size={14}/> تسجيل الخروج</button> : preview ? <button className="account-exit" onClick={exitPreview}><UserRound size={14}/> تسجيل الدخول</button> : null}
    </div>}
  </div>;
}

function AuthEntry({ onPreview, onBack }: { onPreview: () => void; onBack: () => void }) {
  const [mode, setMode] = useState<"signin" | "reset">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      if (!isTradveraOwnerEmail(email)) {
        setMessage({ tone: "error", text: "هذا الإصدار خاص بمالك Tradvera؛ هذا البريد غير مصرح له." });
        return;
      }
      if (mode === "reset") {
        await sendPasswordResetEmail(auth, email.trim());
        setMessage({ tone: "ok", text: "أرسلنا رابط تغيير كلمة المرور إلى بريدك." });
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
    } catch (cause) {
      setMessage({ tone: "error", text: authError(cause) });
    } finally { setBusy(false); }
  };
  return <main className="auth-entry" dir="rtl">
    <button className="auth-public-back" onClick={onBack}><ArrowLeft size={15}/><span>العودة إلى الصفحة التعريفية</span></button>
    <div className="auth-orbit orbit-one"/><div className="auth-orbit orbit-two"/>
    <section className="auth-brand-panel">
      <span className="auth-code">TRADVERA ID · 01</span>
      <div className="auth-mark"><img src="/brand/tradvera-mark.svg" alt="شعار Tradvera"/></div>
      <h1>تعلّمك له ذاكرة.<br/><em>وأدلّتك ملكك.</em></h1>
      <p>حساب واحد يحفظ تقدم الأكاديمية، محاولاتك وبصمة التعلم، بينما تظل بيانات التداول في عالم مستقل تمامًا.</p>
      <div className="auth-contract"><ShieldCheck size={18}/><div><b>ACADEMY DATA BOUNDARY</b><span>لا محفظة · لا أوامر · لا توصيات مالية</span></div></div>
    </section>
    <section className="auth-form-panel">
      <div className="auth-form-head"><small>{mode === "signin" ? "OWNER ACCESS" : "RECOVER ACCESS"}</small><h2>{mode === "signin" ? "ادخل إلى مسارك الخاص" : "استعد كلمة المرور"}</h2><p>{mode === "signin" ? "Private Alpha مقصور على حسابات المالك المعتمدة." : "الاستعادة متاحة للبريدين الإداريين فقط."}</p></div>
      <form onSubmit={submit}>
        <label><span>البريد الإلكتروني · EMAIL</span><div><AtSign size={15}/><input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com"/></div></label>
        {mode !== "reset" && <label><span>كلمة المرور · PASSWORD</span><div><KeyRound size={15}/><input type="password" required minLength={6} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••"/></div></label>}
        {message && <div className={`auth-message is-${message.tone}`}>{message.tone === "ok" && <Check size={14}/>}<span>{message.text}</span></div>}
        <button className="auth-primary" disabled={busy}>{busy ? "لحظة…" : mode === "signin" ? "تسجيل الدخول" : "إرسال رابط الاستعادة"}<ArrowLeft size={15}/></button>
      </form>
      <div className="auth-mode-row">
        {mode !== "signin" && <button onClick={() => { setMode("signin"); setMessage(null); }}>لدي حساب</button>}
        {mode !== "reset" && <button onClick={() => { setMode("reset"); setMessage(null); }}>نسيت كلمة المرور</button>}
      </div>
      {localPreviewAllowed() && <button className="auth-preview" onClick={onPreview}>استكشف المعاينة المحلية بدون حساب <ArrowLeft size={14}/><small>LOCAL DEVELOPMENT ONLY · لا تظهر على tradvera.com</small></button>}
    </section>
  </main>;
}

function AuthLoading() {
  return <div className="auth-loading" dir="rtl"><div className="auth-mark"><img src="/brand/tradvera-mark.svg" alt="شعار Tradvera"/></div><p>نستعيد جلسة Tradvera الآمنة…</p></div>;
}

function authError(cause: unknown) {
  const code = typeof cause === "object" && cause && "code" in cause ? String(cause.code) : "";
  if (code.includes("email-already-in-use")) return "هذا البريد مرتبط بحساب بالفعل؛ استخدم تسجيل الدخول.";
  if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("user-not-found")) return "البريد أو كلمة المرور غير صحيحة.";
  if (code.includes("weak-password")) return "اختر كلمة مرور أقوى من ستة أحرف على الأقل.";
  if (code.includes("too-many-requests")) return "محاولات كثيرة؛ انتظر قليلًا ثم أعد المحاولة.";
  if (code.includes("network-request-failed")) return "تعذر الاتصال الآن؛ تحقق من الإنترنت وحاول مجددًا.";
  if (code.includes("admin-restricted-operation")) return "إنشاء الحسابات مقفول؛ الحسابات يضيفها مدير Tradvera فقط.";
  return "تعذر إكمال الطلب الآن. لم نفقد أي تقدم محلي.";
}
